
import { TTFEpicEngine } from "./ttf-epic-core.js";
import { TTFShopifyCartAdapter } from "./ttf-shopify-cart-adapter.js";
import { TTF_EPIC_MASTER } from "./ttf-epic-master.js";
import { TTF_EPIC_PRODUCTION_SPEC } from "./ttf-epic-production-spec.js";

const EXPECTED_PRODUCT_ID = "8586267787490";
const EXPECTED_VARIANT_ID = "48127733399778";

function initEpicRoot(root) {
  if (!root || root.dataset.epicInitialized === "true") return;
  root.dataset.epicInitialized = "true";

  const productMeta = JSON.parse(root.querySelector("[data-epic-product-json]").textContent);
  const engine = new TTFEpicEngine(TTF_EPIC_MASTER, TTF_EPIC_PRODUCTION_SPEC);
  const cartAdapter = new TTFShopifyCartAdapter(TTF_EPIC_MASTER);

  const q = name => root.querySelector(`[data-epic="${name}"]`);
  const wrap = name => root.querySelector(`[data-wrap="${name}"]`);
  let previousState = {};

  function setOptions(el, rows) {
    const old = el.value;
    el.innerHTML = "";
    rows.forEach(row => {
      const o = document.createElement("option");
      o.value = row.value;
      o.textContent = row.label;
      el.appendChild(o);
    });
    if (rows.some(r => r.value === old)) el.value = old;
  }

  function currentState() {
    return {
      frame: q("frame").value,
      purchase_mode: q("purchase").value,
      geometry: q("geometry").value,
      variant: q("variant").value || "full_main",
      preorder_acknowledged: q("preorder").checked || undefined,
      rts_choice: q("rts").value || undefined,
      change_main_finish: q("changeFinish").value === "true",
      main_finish: q("changeFinish").value === "true" ? q("finish").value : undefined,
      customization: {
        enabled: q("customize").value === "true",
        style: q("customize").value === "true" ? (q("style").value || undefined) : undefined,
        color_method: q("customize").value === "true" ? (q("method").value || undefined) : undefined,
        primary_color: q("customize").value === "true" ? (q("primary").value || undefined) : undefined,
        band_color: q("customize").value === "true" ? (q("band").value || undefined) : undefined,
      },
      topcoat: q("topcoat").value,
      glitter_treatment: q("glitter").value || undefined,
      comments: q("comments").value || undefined
    };
  }

  function refreshPurchaseModes() {
    const frame = q("frame").value;
    setOptions(q("purchase"), engine.purchaseModes(productMeta, frame).map(m => ({
      value: m, label: m.replaceAll("_", " ")
    })));
  }

  function refreshGeometries() {
    const frame = q("frame").value;
    setOptions(q("geometry"), engine.availableGeometries(frame).map(g => ({
      value: g.id, label: g.label
    })));
  }

  function refreshVariants() {
    setOptions(q("variant"), engine.availableVariants(
      q("frame").value,
      q("geometry").value,
      q("purchase").value
    ).map(v => ({ value: v.id, label: v.label })));
  }

  function resolveCurrentState() {
    // The UI controls already represent the fully resolved current state.
    // Do NOT run transition invalidation here: sanitize() is designed for a parent
    // change before descendants are re-selected. Running it after the DOM has
    // already repopulated Casper -> Case incorrectly clears the valid geometry.
    const state = currentState();

    // Prune values that are irrelevant in the CURRENT resolved state.
    const vis = engine.visibleControls(productMeta, state);

    if (!vis.preorder_ack) delete state.preorder_acknowledged;
    if (!vis.rts) delete state.rts_choice;

    if (!vis.finish_swatches) delete state.main_finish;

    if (!vis.customization_style) {
      state.customization = { enabled: !!state.customization?.enabled };
    } else {
      if (!vis.customization_color_method) {
        delete state.customization.color_method;
        delete state.customization.primary_color;
        delete state.customization.band_color;
      } else {
        if (!vis.customization_color_entry) delete state.customization.primary_color;
        if (!vis.band_color) delete state.customization.band_color;
      }
    }

    if (!vis.glitter_treatment) delete state.glitter_treatment;

    return state;
  }

  function render() {
    const state = resolveCurrentState();
    const vis = engine.visibleControls(productMeta, state);

    // Always show exactly what Epic read from the live controls first.
    q("liveState").textContent = JSON.stringify(state, null, 2);

    wrap("variant").classList.toggle("ttf-hidden", !vis.variant);
    wrap("preorder").classList.toggle("ttf-hidden", !vis.preorder_ack);
    wrap("rts").classList.toggle("ttf-hidden", !vis.rts);
    wrap("finish").classList.toggle("ttf-hidden", !vis.finish_swatches);
    wrap("style").classList.toggle("ttf-hidden", !vis.customization_style);
    wrap("method").classList.toggle("ttf-hidden", !vis.customization_color_method);
    wrap("primary").classList.toggle("ttf-hidden", !vis.customization_color_entry);
    wrap("band").classList.toggle("ttf-hidden", !vis.band_color);
    wrap("glitter").classList.toggle("ttf-hidden", !vis.glitter_treatment);

    try {
      const legacy = engine.exportLegacy(productMeta, state);
      const actions = engine.commercialActions(productMeta, state);
      const payload = cartAdapter.buildCartPayload({
        mainVariantId: productMeta.variantId,
        mainQuantity: 1,
        legacyProperties: legacy,
        commercialActions: actions
      });

      q("legacy").textContent = JSON.stringify(legacy, null, 2);
      q("actions").textContent = JSON.stringify(actions, null, 2);
      q("payload").textContent = JSON.stringify(payload, null, 2);
    } catch (err) {
      console.error("EPIC render translation error", err, state);
      q("legacy").textContent = "ERROR: " + err.message;
      q("actions").textContent = "[]";
      q("payload").textContent = "ERROR: " + err.message;
    }

    previousState = structuredClone(state);
  }

  function updateAfterChange(el) {
    if (el.dataset.epic === "frame") {
      refreshPurchaseModes();
      refreshGeometries();
      refreshVariants();
    } else if (el.dataset.epic === "purchase") {
      refreshVariants();
    } else if (el.dataset.epic === "geometry") {
      refreshVariants();
    }
    render();
  }

  window.TTFEpicTest = window.TTFEpicTest || {};
  window.TTFEpicTest.getState = () => structuredClone(currentState());
  window.TTFEpicTest.render = () => render();
  window.TTFEpicTest.getLegacy = () => engine.exportLegacy(productMeta, resolveCurrentState());

  async function addToCart() {
    render(); // force one final DOM -> state -> payload sync
    const state = resolveCurrentState();

    if (String(productMeta.id) !== EXPECTED_PRODUCT_ID || String(productMeta.variantId) !== EXPECTED_VARIANT_ID) {
      q("status").innerHTML =
        `<p class="ttf-error">STOP: product/variant mismatch. Expected ${EXPECTED_PRODUCT_ID}/${EXPECTED_VARIANT_ID}, got ${productMeta.id}/${productMeta.variantId}.</p>`;
      return;
    }

    const legacy = engine.exportLegacy(productMeta, state);
    const actions = engine.commercialActions(productMeta, state);
    const payload = cartAdapter.buildCartPayload({
      mainVariantId: productMeta.variantId,
      legacyProperties: legacy,
      commercialActions: actions
    });

    const errors = cartAdapter.validatePayload(payload);
    if (errors.length) {
      q("status").innerHTML = `<p class="ttf-error">${errors.join("<br>")}</p>`;
      return;
    }

    q("add").disabled = true;
    q("add").textContent = "ADDING...";
    try {
      await cartAdapter.addToShopifyCart(payload);
      q("status").innerHTML =
        `<p class="ttf-ok">✅ Epic added the configuration to the REAL Shopify cart.</p>
         <p><a href="/cart">Open cart and inspect it →</a></p>`;
    } catch (err) {
      console.error(err);
      q("status").innerHTML = `<p class="ttf-error">❌ ${err.message}</p>`;
    } finally {
      q("add").disabled = false;
      q("add").textContent = "ADD EPIC TEST TO REAL CART";
    }
  }

  setOptions(q("frame"), Object.keys(TTF_EPIC_MASTER.frames).map(x => ({ value:x, label:x })));
  refreshPurchaseModes();
  refreshGeometries();
  refreshVariants();
  render();

  root.querySelectorAll("[data-epic]").forEach(el => {
    if (el === q("add") || el === q("syncDebug")) return;
    el.addEventListener("change", () => updateAfterChange(el));
    el.addEventListener("input", () => render());
  });

  root.addEventListener("change", event => {
    const el = event.target.closest("[data-epic]");
    if (!el || el === q("add") || el === q("syncDebug")) return;
    updateAfterChange(el);
  });

  root.addEventListener("input", event => {
    const el = event.target.closest("[data-epic]");
    if (!el || el === q("add") || el === q("syncDebug")) return;
    render();
  });

  const details = root.querySelector("details");
  if (details) {
    details.addEventListener("toggle", () => {
      if (details.open) render();
    });
  }

  const syncTimer = setInterval(() => {
    if (!document.documentElement.contains(root)) {
      clearInterval(syncTimer);
      return;
    }
    render();
  }, 500);

  q("syncDebug")?.addEventListener("click", () => render());
  q("add").addEventListener("click", addToCart);
}

function initAllEpicRoots(scope = document) {
  scope.querySelectorAll("[data-ttf-epic-test]").forEach(initEpicRoot);
}

initAllEpicRoots();

// Shopify Theme Editor dynamically reloads sections without reloading the page.
// Reinitialize the exact newly-loaded section.
document.addEventListener("shopify:section:load", event => {
  if (event.target.matches?.("[data-ttf-epic-test]")) {
    initEpicRoot(event.target);
  } else {
    initAllEpicRoots(event.target);
  }
});
